import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Upload, Building, Mail, MapPin, Phone, Save, X } from 'lucide-react';
import { ApiService } from '../services/api';
import { getStaticFileUrl } from '../utils/backendUrl';
import toast from 'react-hot-toast';

const companyInfoSchema = z.object({
  companyName: z.string().min(2, 'Company name must be at least 2 characters'),
  companyEmail: z.string().email('Invalid email address'),
  companyAddress: z.string().min(10, 'Company address must be at least 10 characters'),
  companyPhone: z.string().min(10, 'Phone number must be at least 10 characters'),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(8, 'Current password is required'),
  newPassword: z.string().min(8, 'New password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type CompanyInfoForm = z.infer<typeof companyInfoSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

interface Company {
  id: string;
  companyName: string;
  companyEmail: string;
  companyAddress: string;
  companyPhone: string;
  logoUrl?: string;
  companyWallet: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const CompanyInfo: React.FC = () => {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const {
    register: registerInfo,
    handleSubmit: handleSubmitInfo,
    formState: { errors: infoErrors },
    reset: resetInfo,
  } = useForm<CompanyInfoForm>({
    resolver: zodResolver(companyInfoSchema),
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
    reset: resetPassword,
  } = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  });

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      setLoading(true);
      const companyData = await ApiService.getCompanyProfile();
      setCompany(companyData);
      resetInfo({
        companyName: companyData.companyName,
        companyEmail: companyData.companyEmail,
        companyAddress: companyData.companyAddress,
        companyPhone: companyData.companyPhone,
      });
    } catch (error) {
      console.error('Failed to fetch company info:', error);
      toast.error('Failed to load company information');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitInfo = async (data: CompanyInfoForm) => {
    try {
      setSaving(true);
      const updatedCompany = await ApiService.updateCompanyProfile(data);
      setCompany(updatedCompany);
      toast.success('Company information updated successfully');
    } catch (error) {
      console.error('Failed to update company info:', error);
      toast.error('Failed to update company information');
    } finally {
      setSaving(false);
    }
  };

  const onSubmitPassword = async (data: PasswordForm) => {
    try {
      setSaving(true);
      await ApiService.updateCompanyPassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      toast.success('Password updated successfully');
      setShowPasswordForm(false);
      resetPassword();
    } catch (error) {
      console.error('Failed to update password:', error);
      toast.error('Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
        console.log(file.type);
      toast.error('Invalid file type. Please upload a JPEG, PNG, GIF, WebP, or SVG image.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size too large. Maximum size is 5MB.');
      return;
    }

    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('logo', file);
      
      const result = await ApiService.uploadCompanyLogo(formData);
      setCompany(prev => prev ? { ...prev, logoUrl: result.logoUrl } : null);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No company information found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Company Information</h1>
        <p className="mt-1 text-sm text-gray-400">
          Manage your company profile and settings
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Company Logo Section */}
        <div className="lg:col-span-1">
          <div className="bg-[#182233] shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-white mb-4">Company Logo</h3>
            <div className="text-center flex flex-col items-center">
              {company.logoUrl ? (
                <div className="relative inline-block">
                  <img
                    src={getStaticFileUrl(company.logoUrl)}
                    alt="Company Logo"
                    className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                  />
                  <button
                    onClick={() => document.getElementById('logo-upload')?.click()}
                    disabled={uploadingLogo}
                    className="absolute -top-2 -right-2 bg-blue-600 text-white rounded-full p-2 hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Upload className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-32 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center mx-auto">
                  <div className="text-center">
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No logo</p>
                  </div>
                </div>
              )}
              
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              
              <button
                onClick={() => document.getElementById('logo-upload')?.click()}
                disabled={uploadingLogo}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#2777EF] hover:bg-[#0053E2] disabled:opacity-50"
              >
                {uploadingLogo ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    {company.logoUrl ? 'Change Logo' : 'Upload Logo'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Company Information Form */}
        <div className="lg:col-span-2">
          <div className="bg-[#182233] shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-600">
              <h3 className="text-lg font-medium text-white">Company Details</h3>
            </div>
            <form onSubmit={handleSubmitInfo(onSubmitInfo)} className="p-6 space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-400">
                    Company Name
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...registerInfo('companyName')}
                      type="text"
                      className="appearance-none rounded-md relative block w-full pl-10 pr-3 py-2 bg-[#567095]/20 border-0 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
                      placeholder="Enter company name"
                    />
                  </div>
                  {infoErrors.companyName && (
                    <p className="mt-1 text-sm text-red-400">{infoErrors.companyName.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="companyEmail" className="block text-sm font-medium text-gray-400">
                    Company Email
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...registerInfo('companyEmail')}
                      type="email"
                      className="appearance-none rounded-md relative block w-full pl-10 pr-3 py-2 bg-[#567095]/20 border-0 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
                      placeholder="Enter company email"
                    />
                  </div>
                  {infoErrors.companyEmail && (
                    <p className="mt-1 text-sm text-red-400">{infoErrors.companyEmail.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="companyAddress" className="block text-sm font-medium text-gray-400">
                  Company Address
                </label>
                <div className="mt-1 relative">
                  <div className="absolute top-3 left-3 flex items-center pointer-events-none">
                    <MapPin className="h-5 w-5 text-gray-400" />
                  </div>
                  <textarea
                    {...registerInfo('companyAddress')}
                    rows={3}
                    className="appearance-none rounded-md relative block w-full pl-10 pr-3 py-2 bg-[#567095]/20 border-0 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
                    placeholder="Enter company address"
                  />
                </div>
                {infoErrors.companyAddress && (
                  <p className="mt-1 text-sm text-red-400">{infoErrors.companyAddress.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="companyPhone" className="block text-sm font-medium text-gray-400">
                  Company Phone
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...registerInfo('companyPhone')}
                    type="tel"
                    className="appearance-none rounded-md relative block w-full pl-10 pr-3 py-2 bg-[#567095]/20 border-0 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
                    placeholder="Enter company phone"
                  />
                </div>
                {infoErrors.companyPhone && (
                  <p className="mt-1 text-sm text-red-400">{infoErrors.companyPhone.message}</p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#2777EF] hover:bg-[#0053E2] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2777EF] disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Password Change Section */}
      <div className="bg-[#182233] shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-600">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">Change Password</h3>
            <button
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              className="text-sm text-[#2777EF] hover:text-[#0053E2]"
            >
              {showPasswordForm ? 'Cancel' : 'Change Password'}
            </button>
          </div>
        </div>

        {showPasswordForm && (
          <form onSubmit={handleSubmitPassword(onSubmitPassword)} className="p-6 space-y-6">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                Current Password
              </label>
              <div className="mt-1 relative">
                <input
                  {...registerPassword('currentPassword')}
                  type={showCurrentPassword ? 'text' : 'password'}
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter current password"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              {passwordErrors.currentPassword && (
                <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <div className="mt-1 relative">
                  <input
                    {...registerPassword('newPassword')}
                    type={showNewPassword ? 'text' : 'password'}
                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter new password"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                {passwordErrors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <div className="mt-1 relative">
                  <input
                    {...registerPassword('confirmPassword')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Confirm new password"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                {passwordErrors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 sm:space-x-0">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  resetPassword();
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Update Password
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CompanyInfo;
